import { lazy, type ComponentType } from 'react';

const RELOAD_FLAG = 'chunk-reload-attempted';

/**
 * React.lazy with a stale-deploy guard.
 *
 * Tabs in the facility stay open for days. After a deploy, the hashed chunk
 * filenames an old tab knows about no longer exist, so the first navigation
 * to a lazy route would 404 and crash the route. When a chunk fails to load
 * we reload the page once (picking up the new index.html + chunk manifest);
 * the sessionStorage flag prevents a reload loop if the failure persists.
 */
export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const mod = await factory();
      sessionStorage.removeItem(RELOAD_FLAG);
      return mod;
    } catch (err) {
      if (!sessionStorage.getItem(RELOAD_FLAG)) {
        sessionStorage.setItem(RELOAD_FLAG, '1');
        window.location.reload();
        // The reload takes over; keep React suspended meanwhile.
        return new Promise<never>(() => {});
      }
      throw err;
    }
  });
}
